// The place-the-notes editors (voicingPins.ts slice 3, user: "you could click
// and literally put the notes in"). Two pure-SVG controls with their state
// lifted to the inspector: a FRETBOARD on one of the four string layouts the
// engine reads grips through (a fret per string), and a KEYBOARD. Both name
// every placed note against the chord — a chord tone, an admitted tension, or
// an OUTSIDE note, marked but never refused: the editor says what you placed,
// the pin is yours. On a melody lane the written note is LOCKED on top: the
// fretboard's top string carries it and cannot be moved, the keyboard refuses
// a key above it.
//
// A tap PLACES a note (a second tap on it clears it) and a placed note sounds
// as it lands, the way the score editor's on-screen piano does; hearing the
// WHOLE grip is the Play button beside "Use this voicing" — press for the
// chord, hold to strum it, the sheet's own hold-to-strum control (user ruling
// Sep 2026, after a double-press-to-place cut: "still closing on iPhone… can
// playback be like the main chord where you can hold on the play button").

import { useMemo } from 'react';
import { getChordPlayer } from '@/audio/player';
import { chordSymbol, type ChordTypeId } from '@/music/chords';
import { spellVoicingMidis } from '@/music/chordSpelling';
import { pitchClassOf, sharpName } from '@/music/notes';
import { hasFlatNineClashMidis, isMuddy } from '@/music/pianoVoicings';
import { chordOwnsFlatNine } from '@/music/chords';
import {
  PLACEABLE_LAYOUTS,
  fretForMidi,
  placedNoteRole,
  type PlacedNote,
} from '@/music/placedVoicing';
import {
  STRING_LABELS,
  STRING_LAYOUTS,
  frettedMidi,
  noteAtFret,
  pitchClassAtFret,
  type StringLayoutId,
} from '@/music/tuning';
import { PianoKeyboardDiagram } from './PianoKeyboardDiagram';

/** Frets the editor's window shows at once, plus the open-string row. */
export const EDITOR_WINDOW_FRETS = 5;
const MAX_FRET = 20;

/** Sound one fretted note through the guitar pluck (a one-shot, never faded). */
const soundFret = (string: number, fret: number) =>
  void getChordPlayer().playMidi([frettedMidi(string, fret)], 0, 'guitar');

/** Sound one key through the piano voice. */
const soundKey = (midi: number) => void getChordPlayer().playMidi([midi], 0, 'piano');

const W = 200;
const MARGIN = { top: 26, bottom: 22, left: 40, right: 17 };
const ROW_H = 30;

export interface FretboardEditorProps {
  rootDisplay: string;
  chordType: ChordTypeId;
  layout: StringLayoutId;
  onLayoutChange: (layout: StringLayoutId) => void;
  /** Six frets, null = silent; strings outside the layout are ignored. */
  frets: (number | null)[];
  onFretsChange: (frets: (number | null)[]) => void;
  /** First fretted row shown (1-based); the open row always shows. */
  windowStart: number;
  onWindowStartChange: (start: number) => void;
  /** A melody-lane chord: the written note the top string must carry. */
  lockedTopMidi?: number | null;
  /** Curated grips can carry their melody on any sounding string. */
  lockedTopString?: number;
  /** Reusable alternatives currently support the four standard string layouts. */
  allowAllStrings?: boolean;
}

export function FretboardEditor({
  rootDisplay,
  chordType,
  layout,
  onLayoutChange,
  frets,
  onFretsChange,
  windowStart,
  onWindowStartChange,
  lockedTopMidi,
  lockedTopString,
  allowAllStrings = true,
}: FretboardEditorProps) {
  const strings = STRING_LAYOUTS[layout].strings;
  const top =
    layout === 'joe-pass' && lockedTopString != null
      ? lockedTopString
      : strings[strings.length - 1];
  const active = new Set<number>(strings);
  const lockedFret = lockedTopMidi != null ? fretForMidi(top, lockedTopMidi, MAX_FRET) : null;
  const fretboardW = W - MARGIN.left - MARGIN.right;
  const stringSpacing = fretboardW / 5;
  const rows = EDITOR_WINDOW_FRETS + 1; // open row + window
  const height = MARGIN.top + rows * ROW_H + MARGIN.bottom;
  const rootPc = pitchClassOf(rootDisplay);

  const place = (string: number, fret: number) => {
    if (!active.has(string)) return;
    if (string === top && lockedFret != null) return;
    if (layout === 'joe-pass' && lockedTopMidi != null && frettedMidi(string, fret) > lockedTopMidi)
      return;
    const next = [...frets];
    const placing = next[string] !== fret;
    next[string] = placing ? fret : null;
    onFretsChange(next);
    if (placing) soundFret(string, fret);
  };

  const roles = useMemo(
    () =>
      strings.map((s) => {
        const fret = s === top && lockedFret != null ? lockedFret : frets[s];
        if (fret == null) return null;
        return placedNoteRole(pitchClassAtFret(s, fret), rootDisplay, chordType);
      }),
    [strings, top, lockedFret, frets, rootDisplay, chordType],
  );

  const rowY = (row: number) => MARGIN.top + row * ROW_H;
  const fretOfRow = (row: number) => (row === 0 ? 0 : windowStart + row - 1);

  return (
    <div className="fretboard-editor" data-testid="fretboard-editor">
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div className="segmented" role="group" aria-label="Strings">
          {PLACEABLE_LAYOUTS.filter((option) => allowAllStrings || option.id !== 'joe-pass').map(
            (option) => (
              <button
                key={option.id}
                type="button"
                className={layout === option.id ? 'active' : ''}
                aria-pressed={layout === option.id}
                data-testid={`editor-layout-${option.id}`}
                onClick={() => onLayoutChange(option.id)}
              >
                {option.label}
              </button>
            ),
          )}
        </div>
        <div className="segmented" role="group" aria-label="Position">
          <button
            type="button"
            aria-label="Lower frets"
            data-testid="editor-window-down"
            disabled={windowStart <= 1}
            onClick={() => onWindowStartChange(Math.max(1, windowStart - 1))}
          >
            −
          </button>
          <span className="fretboard-editor-position" data-testid="editor-window-start">
            {windowStart}
          </span>
          <button
            type="button"
            aria-label="Higher frets"
            data-testid="editor-window-up"
            disabled={windowStart + EDITOR_WINDOW_FRETS - 1 >= MAX_FRET}
            onClick={() => onWindowStartChange(windowStart + 1)}
          >
            +
          </button>
        </div>
      </div>
      <svg
        className="chord-diagram fretboard-editor-svg"
        viewBox={`0 0 ${W} ${height}`}
        role="group"
        aria-label={`Place the notes of ${chordSymbol(rootDisplay, chordType)}`}
      >
        {/* Strings */}
        {Array.from({ length: 6 }, (_, i) => {
          const x = MARGIN.left + i * stringSpacing;
          return (
            <line
              key={`s${i}`}
              x1={x}
              y1={rowY(1)}
              x2={x}
              y2={rowY(rows)}
              className="cd-string"
              opacity={active.has(i) ? 1 : 0.25}
            />
          );
        })}
        {/* Frets: the nut when the window opens at fret 1, else a plain fret. */}
        {Array.from({ length: EDITOR_WINDOW_FRETS + 1 }, (_, i) => {
          const y = rowY(1 + i);
          return (
            <line
              key={`f${i}`}
              x1={MARGIN.left}
              y1={y}
              x2={MARGIN.left + fretboardW}
              y2={y}
              className={i === 0 && windowStart === 1 ? 'cd-nut' : 'cd-fret'}
            />
          );
        })}
        {windowStart > 1 && (
          <text x={MARGIN.left - 15} y={rowY(1) + ROW_H * 0.5} className="cd-fret-label">
            {windowStart}
          </text>
        )}
        {/* Cells: one tap target per string × row (the open row included). */}
        {Array.from({ length: rows }, (_, row) =>
          Array.from({ length: 6 }, (_, string) => {
            const fret = fretOfRow(row);
            const isActive = active.has(string);
            const locked = string === top && lockedFret != null;
            const placed = locked ? lockedFret === fret : frets[string] === fret;
            const cx = MARGIN.left + string * stringSpacing;
            const cy = row === 0 ? rowY(0) + ROW_H * 0.6 : rowY(row) + ROW_H * 0.5;
            return (
              <g
                key={`c${row}-${string}`}
                className={`fretboard-cell${isActive && !locked ? ' is-active' : ''}`}
                data-testid={isActive ? 'fret-cell' : undefined}
                data-string={string}
                data-fret={fret}
                data-placed={placed || undefined}
                role={isActive && !locked ? 'button' : undefined}
                tabIndex={isActive && !locked ? 0 : undefined}
                aria-label={
                  isActive
                    ? `${STRING_LABELS[string]} string ${fret === 0 ? 'open' : `fret ${fret}`}, ${noteAtFret(string, fret)}`
                    : undefined
                }
                aria-pressed={isActive && !locked ? placed : undefined}
                onClick={isActive && !locked ? () => place(string, fret) : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    place(string, fret);
                  }
                }}
              >
                <rect
                  x={cx - stringSpacing / 2}
                  y={cy - ROW_H / 2}
                  width={stringSpacing}
                  height={ROW_H}
                  fill="transparent"
                />
                {placed && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={10}
                    className={`cd-note${pitchClassAtFret(string, fret) === rootPc ? ' cd-note-root' : ''}${locked ? ' is-locked' : ''}`}
                  />
                )}
                {placed && locked && (
                  <text x={cx} y={cy + 3.5} className="fretboard-lock" textAnchor="middle">
                    ♪
                  </text>
                )}
              </g>
            );
          }),
        )}
        {/* Degree labels under each active string. */}
        {strings.map((s, i) => {
          const role: PlacedNote | null = roles[i];
          if (!role) return null;
          return (
            <text
              key={`l${s}`}
              x={MARGIN.left + s * stringSpacing}
              y={rowY(rows) + 15}
              className={`cd-interval fretboard-role fretboard-role-${role.role}`}
              data-role={role.role}
            >
              {role.label}
            </text>
          );
        })}
      </svg>
      {lockedTopMidi != null && lockedFret == null && (
        <p className="settings-note" data-testid="editor-lock-unreachable">
          The written note is off this layout
        </p>
      )}
    </div>
  );
}

export interface KeyboardEditorProps {
  rootDisplay: string;
  chordType: ChordTypeId;
  midis: number[];
  onMidisChange: (midis: number[]) => void;
  /** A melody-lane chord: the written note stays on top. */
  lockedTopMidi?: number | null;
  low?: number;
  high?: number;
}

export function KeyboardEditor({
  rootDisplay,
  chordType,
  midis,
  onMidisChange,
  lockedTopMidi,
  low = 36,
  high = 84,
}: KeyboardEditorProps) {
  const sounding = useMemo(() => {
    const set = new Set(midis);
    if (lockedTopMidi != null) set.add(lockedTopMidi);
    return [...set].sort((a, b) => a - b);
  }, [midis, lockedTopMidi]);
  const toggle = (midi: number) => {
    if (lockedTopMidi != null && (midi === lockedTopMidi || midi > lockedTopMidi)) return;
    const set = new Set(sounding);
    if (set.has(midi)) set.delete(midi);
    else {
      set.add(midi);
      soundKey(midi);
    }
    onMidisChange([...set].sort((a, b) => a - b));
  };
  const names = spellVoicingMidis(sounding, rootDisplay, chordType, sharpName);
  const roles = sounding.map((m) => placedNoteRole(((m % 12) + 12) % 12, rootDisplay, chordType));
  const muddy = isMuddy(sounding);
  const clash = !chordOwnsFlatNine(chordType) && hasFlatNineClashMidis(sounding);
  return (
    <div className="keyboard-editor" data-testid="keyboard-editor">
      <PianoKeyboardDiagram
        midis={sounding}
        low={low}
        high={high}
        height={72}
        label={`Place the notes of ${chordSymbol(rootDisplay, chordType)}`}
        onKeyClick={toggle}
      />
      <div className="keyboard-editor-notes">
        {sounding.map((m, i) => (
          <span
            key={m}
            className={`keyboard-editor-note fretboard-role-${roles[i].role}`}
            data-role={roles[i].role}
            data-midi={m}
          >
            {names[i]} <span className="muted">{roles[i].label}</span>
            {m === lockedTopMidi ? ' ♪' : ''}
          </span>
        ))}
        {muddy && <span className="keyboard-editor-flag">low</span>}
        {clash && <span className="keyboard-editor-flag">b9</span>}
      </div>
    </div>
  );
}
